import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { useSettingsContext } from 'src/context/theme-settings-context';
import { useSubscription } from 'src/context/subscription-context';
import { useBoolean } from 'src/hooks/use-boolean';
import { useActivePlatform } from 'src/hooks/use-active-platform';
import { RESOURCES, usePermissions } from 'src/hooks/use-permissions';
import { PaymentBanner, HardPaywallModal } from 'src/components/billing-banners';
import { SkipLink } from 'src/components/skip-link';

import { AiOrchestratorProvider } from 'src/ai-orchestrator';
import { AiAssistantLauncher, LazyAiAssistantDrawer } from 'src/views/dashboard/ai-orchestrator';
import { InvitationAcceptDialog } from 'src/views/dashboard/invitation-accept';
            
import { Header } from './header';
import { Main } from './main';
import { Sidebar, SidebarMini } from './sidebar';

import { useBootstrapActivePlatformId, useEnsurePlatformIdInSearchParams } from './hooks';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export function DashboardLayout({ children }: Props) {
  useBootstrapActivePlatformId();
  useEnsurePlatformIdInSearchParams();

  const settings = useSettingsContext();
  const theme = useTheme();
  const nav = useBoolean();

  const lgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const isMini = settings.themeLayout === 'mini';

  const { paymentState, status, paymentBannerDismissed, dismissPaymentBanner } = useSubscription();
  const { canWrite } = usePermissions();
  const { platform } = useActivePlatform();

  const paymentBanner = (() => {
    if (!status) return null;
    if ((paymentState === 'upcoming' && !paymentBannerDismissed) || paymentState === 'overdue') {
      return (
        <PaymentBanner
          variant={paymentState}
          daysOverdue={status.days_overdue}
          daysRemaining={status.days_remaining}
          amountDue={status.amount_due}
          currency={status.currency}
          paymentUrl={status.payment_url}
          canPay={canWrite(RESOURCES.SUBSCRIPTION.MAIN)}
          onDismiss={dismissPaymentBanner}
          sx={{ mx: { xs: 0, lg: -2 } }}
        />
      );
    }

    return null;
  })();

  const renderNavMini = <SidebarMini />;
  const renderNavVertical = <Sidebar openNav={nav.value} onCloseNav={nav.onFalse} />;

  return (
    <AiOrchestratorProvider>
      <SkipLink />
      <Header onOpenNav={nav.onTrue} />

      <Box
        sx={{
          minHeight: 1,
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
        }}
      >
        {isMini && lgUp ? renderNavMini : renderNavVertical}

        <Main data-testid="app-shell" data-platform-id={platform?.id}>
          {paymentBanner}
          {children}
        </Main>
      </Box>

      <AiAssistantLauncher />
      <LazyAiAssistantDrawer />

      <HardPaywallModal open={paymentState === 'blocked'} status={status ?? null} />

      <InvitationAcceptDialog />
    </AiOrchestratorProvider>
  );
}
